import { useContext, useLayoutEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Container, Typography } from '@mui/material'

//components
import BottomButton from 'components/common/BottomButton'
import ConfirmOrderModal from 'components/ConfirmOrderModal'
import Layout from 'components/layout/Details'
import PageInner from 'components/layout/PageInner'
import PriceText from 'components/giftCards/PriceText'
import NotFound from '../NotFound'
//constants
import { ERROR_TEXT, LOCAL_STORAGE } from 'constants/index'
//contexts
import AuthContext from 'context/AuthContext'
import LoaderContext from 'context/LoaderContext'
import useOrderGiftCardMutation from 'hooks/useOrderGiftCardMutation'
//hooks
import useQueryGetGiftCardById from 'hooks/useQueryGetGiftCardById'
import useLocalStorage from 'hooks/useLocalStorage'
//styles
import './index.css'
//types
import { AuthContextType } from 'types'
//utils
import { formatNumber, isUserFrozen, isUserOutOfAttempts, isUserVerified } from 'utils/utils'

/**
 * A page to display the Gift Card for user to purchase
 * The main parameters used to determine the value of a gift card are ID and denomination
 *
 * @component
 * @returns {ReactNode} A React element that renders gift card details page
 */
const GiftCardsDetails = () => {
  const { user } = useContext(AuthContext) as AuthContextType
  const { isLoading, showLoader, hideLoader } = useContext(LoaderContext)
  const [modal, setModal] = useState(false)
  const [isValidDenomination, setValidDenomination] = useState(true)
  const [piggyBanksValue, setPiggyBanksValue] = useState(0)

  const navigate = useNavigate()
  const { setItem } = useLocalStorage()
  const { createOrderMutate } = useOrderGiftCardMutation()

  const { giftCardId, denomination } = useParams()

  const { giftCard, isFetching, timeLimit } = useQueryGetGiftCardById(
    giftCardId,
    denomination,
    setValidDenomination,
    setPiggyBanksValue
  )

  useLayoutEffect(() => {
    isFetching ? showLoader() : hideLoader()
  }, [isFetching])

  const showConfirmOrderModal = () => setModal(true)
  const hideConfirmOrderModal = () => setModal(false)

  const handleOrderConfirm = async () => {
    const isVerified = isUserVerified(user)
    const outOfAttempts = isUserOutOfAttempts(user)
    const isFrozen = isUserFrozen(user)
    hideConfirmOrderModal()
    if (isVerified) {
      hideConfirmOrderModal()
      showLoader()
      if (isFrozen) {
        hideLoader()
        toast.error(ERROR_TEXT.account_frozen_gift_card, { duration: 10000 })
        return
      }
      if (!denomination) {
        toast.error('Invalid Gift Card denomination')
        return
      }
      return createOrderMutate({ giftCardId: giftCard!.id, denomination })
    }

    if (outOfAttempts) {
      navigate('/verification/result')
      return
    }

    setItem(LOCAL_STORAGE.GIFT_CARD_ID, `${giftCard!.id}`)
    setItem(LOCAL_STORAGE.GIFT_CARD_DENOMINATION, `${denomination}`)
    navigate('/verification')
  }

  const SubmitButtonText = () => (
    <Typography variant="h4" className="giftCardDetailsSubmitButtonText">
      Buy:{' '}
      <PriceText
        userBalance={user?.piggyBanks}
        giftCardValue={piggyBanksValue}
        value={formatNumber(piggyBanksValue)}
        isPig={true}
      />
    </Typography>
  )

  if (!isValidDenomination || (!isLoading && !giftCard)) {
    return <NotFound />
  }

  return (
    <PageInner>
      {giftCard && (
        <ConfirmOrderModal
          showModal={modal}
          hideModal={hideConfirmOrderModal}
          giftCardName={giftCard?.name}
          denomination={denomination}
          formattedPiggiesValue={formatNumber(piggyBanksValue)}
          piggyBanksValue={piggyBanksValue}
          timeLimit={timeLimit}
          handleOrderConfirm={handleOrderConfirm}
        />
      )}
      <Layout title="Redeem Gift Card">
        <Container maxWidth="sm" sx={styles.container}>
          <div className={`giftCardDetailsInner ${isLoading ? 'blured' : ''}`}>
            <div className="giftCardDetailsContent">
              <img alt={giftCard?.name} src={giftCard?.image} className="giftCardDetailsImage" />
              <Typography variant="h2">{giftCard?.name} Gift Card</Typography>
              <Typography variant="h1">${denomination} USD</Typography>
              <Typography
                variant="body2"
                className="giftCardDetailsDescription"
                dangerouslySetInnerHTML={{ __html: `<small>${giftCard?.terms}</small>` }}
              />
            </div>
            <BottomButton handleClick={showConfirmOrderModal} style={styles.closeButton}>
              <SubmitButtonText />
            </BottomButton>
          </div>
        </Container>
      </Layout>
    </PageInner>
  )
}

const styles = {
  container: {
    padding: 0,
  },
  closeButton: {
    border: '2px solid rgb(251, 146, 60)',
    backgroundColor: '#ffffff',
    color: '#000000',
    marginBottom: '20px',
  },
}

export default GiftCardsDetails
